import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RefreshCw, Clock, MapPin, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { planDayApi } from "@/lib/accountApi";
import { gbp } from "@/lib/format";
import { worldOf } from "@/lib/worlds";
import { ListingCard } from "@/components/ListingCard";

function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Item({ it }) {
  if (it.kind === "gap" || it.kind === "leave") {
    return (
      <li className="flex gap-4 py-3 text-sm">
        <span className="w-14 shrink-0 font-mono-num text-muted-foreground">{it.time}</span>
        <div>
          <p className="font-medium">{it.title}</p>
          {it.note && <p className="text-muted-foreground">{it.note}</p>}
        </div>
      </li>
    );
  }
  const w = worldOf(it.module);
  return (
    <li className="flex gap-4 py-3">
      <span className="w-14 shrink-0 pt-1 font-mono-num text-sm text-muted-foreground">{it.time}</span>
      <div className="flex min-w-0 flex-1 gap-3 rounded-2xl border border-border bg-card p-3">
        {it.image && <img src={it.image} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />}
        <div className="min-w-0 flex-1">
          <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize" style={{ background: w.accentSoft, color: w.accent }}>{w.name}</span>
          <p className="mt-1 truncate font-semibold">{it.title}</p>
          <p className="truncate text-sm text-muted-foreground">{it.provider}{it.location ? ` · ${it.location}` : ""}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {it.end_time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {it.time}–{it.end_time}</span>}
            {it.leave_by && <span>Leave by {it.leave_by}{it.route?.summary ? ` · ${it.route.summary}` : ""}</span>}
            {it.weather?.label && <span className="inline-flex items-center gap-1"><Sun size={12} /> {it.weather.emoji} {it.weather.label}{it.weather.temp != null ? ` ${Math.round(it.weather.temp)}°` : ""}</span>}
          </div>
          {Array.isArray(it.flags) && it.flags.length > 0 && <p className="mt-1 text-xs text-amber-700">{it.flags.map((f) => f.message || f).join(" · ")}</p>}
        </div>
      </div>
    </li>
  );
}

export default function PlanDay() {
  usePageMeta("Plan my day", "Your bookings for the day stitched into one plan, with leave-by times and the weather.");
  const [day, setDay] = useState("today");
  const [plan, setPlan] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) => {
    setBusy(true);
    if (!refresh) setPlan(undefined);
    planDayApi.get({ date: day === "today" ? undefined : tomorrowIso(), refresh: refresh || undefined }).then(setPlan).catch(() => setPlan(null)).finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl font-bold">Plan my day</h1>
          <p className="mt-1 text-muted-foreground">Everything you've booked, in order, with when to leave and what the weather's doing.</p>
        </div>
        <div className="flex items-center gap-2">
          {["today", "tomorrow"].map((d) => (
            <button key={d} type="button" onClick={() => setDay(d)} aria-pressed={day === d} data-testid={`plan-${d}`} className={`rounded-full border px-4 py-1.5 text-sm font-medium capitalize ${day === d ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{d}</button>
          ))}
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => load(true)} disabled={busy} aria-label="Refresh"><RefreshCw size={16} className={busy ? "animate-spin" : ""} /></Button>
        </div>
      </div>

      {plan === undefined ? (
        <div className="mt-6 space-y-3"><Skeleton className="h-36 rounded-2xl" /><Skeleton className="h-24 rounded-2xl" /><Skeleton className="h-24 rounded-2xl" /></div>
      ) : plan === null ? (
        <p className="mt-10 text-center text-muted-foreground">Couldn't build your plan right now.</p>
      ) : (
        <>
          <section className="mt-6 rounded-3xl p-6 text-white" style={{ background: "linear-gradient(135deg, var(--fable), var(--provider))" }} data-testid="plan-summary">
            <p className="text-sm font-medium text-white/80">{plan.label} · {new Date(plan.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
            <h2 className="mt-1 font-serif-display text-2xl font-bold">{plan.title || (plan.free_day ? "A free day" : `${plan.count} thing${plan.count === 1 ? "" : "s"} on today`)}</h2>
            {plan.summary && <p className="mt-2 max-w-2xl text-white/90">{plan.summary}</p>}
            {plan.day_weather?.label && <p className="mt-2 text-sm text-white/85">{plan.day_weather.emoji} {plan.day_weather.label}{plan.day_weather.temp_max != null ? ` · up to ${Math.round(plan.day_weather.temp_max)}°` : ""}</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/yasmin/chat"><Button variant="secondary" className="rounded-full"><Sparkles size={15} className="mr-1.5" /> Ask Yasmin to tweak it</Button></Link>
              {plan.origin?.label && <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs"><MapPin size={12} /> From {plan.origin.label}</span>}
            </div>
            {plan.ai && <p className="mt-3 text-[11px] text-white/70">Written by Fable + AI · {Array.isArray(plan.attribution) ? plan.attribution.join(" · ") : ""}</p>}
          </section>

          {plan.items?.length > 0 && (
            <section className="mt-6">
              <h2 className="font-bold">Your day</h2>
              <ul className="mt-2 divide-y divide-border" data-testid="plan-items">{plan.items.map((it, i) => <Item key={it.booking_id ? `${it.kind}-${it.booking_id}` : i} it={it} />)}</ul>
            </section>
          )}

          {Array.isArray(plan.tips) && plan.tips.length > 0 && (
            <section className="mt-6 rounded-2xl border border-border bg-card p-5">
              <h2 className="font-bold">Ideas for {plan.label?.toLowerCase() || "today"}</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-foreground/85">{plan.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </section>
          )}

          {Array.isArray(plan.suggestions) && plan.suggestions.length > 0 && (
            <section className="mt-6">
              <h2 className="font-bold">Real, highly rated options near you</h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {plan.suggestions.map((s) => <ListingCard key={s.id} listing={{ ...s, price_from: s.price_from ?? s.price, price_unit: s.price_unit || "booking" }} />)}
              </div>
              {plan.suggestions[0]?.price_from != null && <p className="sr-only">from {gbp(plan.suggestions[0].price_from)}</p>}
            </section>
          )}

          {Array.isArray(plan.upcoming) && plan.upcoming.length > 0 && (
            <section className="mt-6">
              <h2 className="font-bold">Coming up</h2>
              <ul className="mt-2 space-y-2 text-sm">{plan.upcoming.map((u, i) => <li key={i} className="rounded-2xl border border-border bg-card px-4 py-3">{u.title || u.listing_title} · {u.date}{u.time_slot ? ` ${u.time_slot}` : ""}</li>)}</ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
